import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus, HelpCircle, X } from 'lucide-react';
import {
  deliveryMethodService,
  DeliveryMethodDto,
  BulkUpdateDeliveryMethodRequest,
  LayoutLookupDto
} from '../../../../../services/deliveryMethodService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { WarehouseTable, useInlineEdit } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction } from '../../../../shared/table/types/table';
import { DeliveryMethodSidePanel } from './DeliveryMethodSidePanel';
import { DeliveryMethodCreateModal } from './DeliveryMethodCreateModal';

interface DeliveryMethodDataTableProps {
  refreshTrigger?: number;
}

export const DeliveryMethodDataTable: React.FC<DeliveryMethodDataTableProps> = ({
  refreshTrigger
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo || '';

  const [data, setData] = useState<DeliveryMethodDto[]>([]);
  const [layouts, setLayouts] = useState<LayoutLookupDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const inlineEditConfig = useInlineEdit<DeliveryMethodDto>({
    getRowId: (row) => row.id,
    onSave: async (originalRow, editedData) => {
      const request: BulkUpdateDeliveryMethodRequest = {
        companyNo: originalRow.companyNo,
        deliveryMethodNo: (editedData.deliveryMethodNo as number) ?? originalRow.deliveryMethodNo,
        description: (editedData.description as string) ?? originalRow.description,
        externalID: (editedData.externalID as string) ?? originalRow.externalID,
        consignmentSWDefProductNo: (editedData.consignmentSWDefProductNo as string) ?? originalRow.consignmentSWDefProductNo,
        consignmentSWAdditionalCode: (editedData.consignmentSWAdditionalCode as string) ?? originalRow.consignmentSWAdditionalCode,
        shippingLabelsYesNo: (editedData.shippingLabelsYesNo as boolean) ?? originalRow.shippingLabelsYesNo,
        shippingLabelsLayoutNo: (editedData.shippingLabelsLayoutNo as number) ?? originalRow.shippingLabelsLayoutNo,
        labelExportFolder: (editedData.labelExportFolder as string) ?? originalRow.labelExportFolder,
        labelExportFilename: (editedData.labelExportFilename as string) ?? originalRow.labelExportFilename,
        packageSlipERPYesNo: (editedData.packageSlipERPYesNo as boolean) ?? originalRow.packageSlipERPYesNo,
        shippingLabelsERPYesNo: (editedData.shippingLabelsERPYesNo as boolean) ?? originalRow.shippingLabelsERPYesNo,
        consignmentNoteERPYesNo: (editedData.consignmentNoteERPYesNo as boolean) ?? originalRow.consignmentNoteERPYesNo,
        singleParcelDelivery: (editedData.singleParcelDelivery as boolean) ?? originalRow.singleParcelDelivery,
        autoBookYesNo: (editedData.autoBookYesNo as boolean) ?? originalRow.autoBookYesNo,
        requirePackageDetailsYesNo: (editedData.requirePackageDetailsYesNo as boolean) ?? originalRow.requirePackageDetailsYesNo,
        consignmentSWDefProductNoIsNShiftCSID: (editedData.consignmentSWDefProductNoIsNShiftCSID as boolean) ?? originalRow.consignmentSWDefProductNoIsNShiftCSID
      };
      await deliveryMethodService.bulkUpdate([request]);
    },
    onSaveSuccess: () => loadData(),
    onSaveError: (err) => setError(err.message || t('manager.modules.adminDelivery.common.errors.saveFailed', {}, 'Feil ved lagring'))
  });

  useEffect(() => {
    loadData();
    deliveryMethodService.getLayouts().then(setLayouts).catch(() => {});
  }, [companyNo]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      loadData();
    }
  }, [refreshTrigger]);

  const loadData = useCallback(async () => {
    if (!companyNo) return;

    setIsLoading(true);
    setError(null);

    try {
      const methods = await deliveryMethodService.getCurrent().catch(() =>
        deliveryMethodService.getAll().catch(() => [])
      );
      setData(methods);
    } catch (err: any) {
      console.error('Error loading delivery methods:', err);
      setError(t('manager.modules.adminDelivery.deliveryMethod.errors.loadFailed', {}, 'Kunne ikke laste leveringsmåter. Prøv igjen.'));
    } finally {
      setIsLoading(false);
    }
  }, [companyNo, t]);

  const columnFactory = createColumnFactory<DeliveryMethodDto>();

  const columns: ColumnDefinition<DeliveryMethodDto>[] = useMemo(() => [
    columnFactory.createNumberColumn('deliveryMethodNo', 'deliveryMethodNo', t('manager.modules.adminDelivery.deliveryMethod.columns.deliveryMethodNo', {}, 'Lev.måte nr.'), {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true,
      formatter: (value: any) => value != null ? String(value) : '-'
    }),

    columnFactory.createTextColumn('description', 'description', t('common.fields.description', {}, 'Beskrivelse'), {
      minWidth: 200,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createTextColumn('externalID', 'externalID', t('manager.modules.adminDelivery.deliveryMethod.fields.externalID', {}, 'Transportør ID'), {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createTextColumn('consignmentSWDefProductNo', 'consignmentSWDefProductNo', t('manager.modules.adminDelivery.deliveryMethod.columns.taProductNo', {}, 'TA Produktnr.'), {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createTextColumn('consignmentSWAdditionalCode', 'consignmentSWAdditionalCode', t('manager.modules.adminDelivery.deliveryMethod.columns.taAdditionalCode', {}, 'TA Tilleggskode'), {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('consignmentSWDefProductNoIsNShiftCSID', 'consignmentSWDefProductNoIsNShiftCSID', t('manager.modules.adminDelivery.deliveryMethod.columns.nshiftProdCSID', {}, 'NShift ProdCSID'), {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createNumberColumn('shippingLabelsLayoutNo', 'shippingLabelsLayoutNo', t('manager.modules.adminDelivery.deliveryMethod.fields.labelLayout', {}, 'Etikettlayout'), {
      minWidth: 180,
      sortable: true,
      filterType: 'both',
      editable: true,
      visible: false,
      ...(layouts.length > 0 ? {
        editType: 'select' as const,
        editOptions: layouts.map(l => ({ value: l.layoutNo, label: `${l.layoutNo} - ${l.description || ''}` }))
      } : {}),
      formatter: (value: any) => {
        if (value == null) return '-';
        const layout = layouts.find(l => l.layoutNo === value);
        return layout ? `${value} - ${layout.description}` : String(value);
      }
    }),

    columnFactory.createTextColumn('labelExportFolder', 'labelExportFolder', t('manager.modules.adminDelivery.deliveryMethod.fields.labelExportFolder', {}, 'Eksportkatalog'), {
      minWidth: 200,
      sortable: true,
      filterType: 'both',
      editable: true,
      visible: false
    }),

    columnFactory.createBooleanColumn('shippingLabelsYesNo', 'shippingLabelsYesNo', t('manager.modules.adminDelivery.deliveryMethod.columns.shippingLabel', {}, 'Fraktetikett'), {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('singleParcelDelivery', 'singleParcelDelivery', t('manager.modules.adminDelivery.deliveryMethod.columns.singleParcel', {}, 'Enkeltkolli'), {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('autoBookYesNo', 'autoBookYesNo', t('manager.modules.adminDelivery.deliveryMethod.columns.autoBook', {}, 'Auto-book'), {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('requirePackageDetailsYesNo', 'requirePackageDetailsYesNo', t('manager.modules.adminDelivery.deliveryMethod.columns.requirePackageDetails', {}, 'Krev kollidetaljer'), {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('packageSlipERPYesNo', 'packageSlipERPYesNo', t('manager.modules.adminDelivery.deliveryMethod.columns.packageSlipERP', {}, 'Pakkseddel ERP'), {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('shippingLabelsERPYesNo', 'shippingLabelsERPYesNo', t('manager.modules.adminDelivery.deliveryMethod.columns.labelERP', {}, 'Etikett ERP'), {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      editable: true
    }),

    columnFactory.createBooleanColumn('consignmentNoteERPYesNo', 'consignmentNoteERPYesNo', t('manager.modules.adminDelivery.deliveryMethod.columns.consignmentNoteERP', {}, 'Fraktbrev ERP'), {
      minWidth: 110,
      sortable: true,
      filterType: 'both',
      editable: true
    })
  ], [layouts, t]);

  const handleBulkEdit = useCallback(() => {
    if (selectedIds.length === 0) {
      alert(t('manager.modules.adminDelivery.deliveryMethod.errors.selectAtLeastOne', {}, 'Velg minst én leveringsmåte for å redigere'));
      return;
    }
    setShowBulkEditModal(true);
  }, [selectedIds.length, t]);

  const tableActions: TableAction<DeliveryMethodDto>[] = useMemo(() => [
    {
      id: 'create',
      label: t('manager.modules.adminDelivery.deliveryMethod.create.title', {}, 'Ny leveringsmåte'),
      icon: Plus,
      onClick: () => setShowCreateModal(true),
      variant: 'primary' as const
    },
    {
      id: 'bulk-edit',
      label: t('manager.modules.adminDelivery.common.maintain', { '0': selectedIds.length }, `Vedlikehold (${selectedIds.length})`),
      icon: Edit,
      onClick: () => handleBulkEdit(),
      disabled: () => selectedIds.length === 0,
      variant: 'primary' as const
    }
  ], [selectedIds.length, handleBulkEdit, t]);

  const selectedRowsMap = useMemo(() => {
    const map: { [key: string]: boolean } = {};
    selectedIds.forEach(id => { map[String(id)] = true; });
    return map;
  }, [selectedIds]);

  // sql = reelt kolonnenavn i DeliveryMethods-tabellen (språk-nøytralt, holdes her).
  // inactive = feltet lagres men konsumeres ikke av backend i dag (legacy/reservert).
  const helpFields: { label: string; sql: string; desc: string; inactive?: boolean }[] = [
    { label: t('manager.modules.adminDelivery.deliveryMethod.fields.externalID', {}, 'Transportør ID'), sql: 'ExternalID', desc: t('manager.modules.adminDelivery.deliveryMethod.helpPanel.externalID', {}, 'Transportør-ID i TA-systemet (f.eks. NShift carrier-kode).') },
    { label: t('manager.modules.adminDelivery.deliveryMethod.columns.taProductNo', {}, 'TA Produktnr.'), sql: 'ConsignmentSWDefProductNo', desc: t('manager.modules.adminDelivery.deliveryMethod.helpPanel.taProductNo', {}, 'Produktkode som sendes til TA. For NShift = ProdConceptID (katalog), eller ProdCSID hvis flagget «NShift ProdCSID» er på.') },
    { label: t('manager.modules.adminDelivery.deliveryMethod.columns.nshiftProdCSID', {}, 'NShift ProdCSID'), sql: 'ConsignmentSWDefProductNoIsNShiftCSID', desc: t('manager.modules.adminDelivery.deliveryMethod.helpPanel.nshiftProdCSID', {}, 'Når på sendes TA-produktnr. som ProdCSID i stedet for ProdConceptID. For interne/custom NShift-transportører uten katalog-produkt.') },
    { label: t('manager.modules.adminDelivery.deliveryMethod.columns.taAdditionalCode', {}, 'TA Tilleggskode'), sql: 'ConsignmentSWAdditionalCode', inactive: true, desc: t('manager.modules.adminDelivery.deliveryMethod.helpPanel.taAdditionalCode', {}, 'Ekstra tjeneste-/tilleggskode for TA. Lagres på leveringsmåten, men leses p.t. ikke av noen TA-provider i .NET-flyten — sendes altså ikke til transportør i dag (legacy/reservert).') },
    { label: t('manager.modules.adminDelivery.deliveryMethod.fields.labelLayout', {}, 'Etikettlayout'), sql: 'ShippingLabelsLayoutNo', inactive: true, desc: t('manager.modules.adminDelivery.deliveryMethod.helpPanel.labelLayout', {}, 'Tiltenkt etikett-layoutvalg, men leses p.t. ikke av backend — har ingen effekt i dagens flyt (reservert).') },
    { label: t('manager.modules.adminDelivery.deliveryMethod.fields.labelExportFolder', {}, 'Eksportkatalog'), sql: 'LabelExportFolder', desc: t('manager.modules.adminDelivery.deliveryMethod.helpPanel.labelExportFolder', {}, 'Filkatalog der etikettfiler legges ved eksport.') },
    { label: t('manager.modules.adminDelivery.deliveryMethod.columns.shippingLabel', {}, 'Fraktetikett'), sql: 'ShippingLabelsYesNo', desc: t('manager.modules.adminDelivery.deliveryMethod.helpPanel.shippingLabel', {}, 'Sender TA-genererte fraktetiketter til fysisk utskrift via PrintNode (DM- ELLER SystemSetup-flagg aktiverer). Av = etiketten lagres kun til disk. Styrer IKKE printerinfo i eksportfilen — det gjør «Etikett ERP» (ShippingLabelsERPYesNo).') },
    { label: t('manager.modules.adminDelivery.deliveryMethod.columns.singleParcel', {}, 'Enkeltkolli'), sql: 'SingleParcelDelivery', desc: t('manager.modules.adminDelivery.deliveryMethod.helpPanel.singleParcel', {}, 'Opprett én sending per kolli — for transportører som kun støtter ett kolli per sending.') },
    { label: t('manager.modules.adminDelivery.deliveryMethod.columns.autoBook', {}, 'Auto-book'), sql: 'AutoBookYesNo', desc: t('manager.modules.adminDelivery.deliveryMethod.helpPanel.autoBook', {}, 'Book sendingen automatisk hos transportør (Apport: save→book). Av = lagre kun som utkast.') },
    { label: t('manager.modules.adminDelivery.deliveryMethod.columns.requirePackageDetails', {}, 'Krev kollidetaljer'), sql: 'RequirePackageDetailsYesNo', desc: t('manager.modules.adminDelivery.deliveryMethod.helpPanel.requirePackageDetails', {}, 'Operatøren må registrere antall kolli + vekt ved avslutning av forsendelse for denne leveringsmåten.') },
    { label: t('manager.modules.adminDelivery.deliveryMethod.columns.packageSlipERP', {}, 'Pakkseddel ERP'), sql: 'PackageSlipERPYesNo', desc: t('manager.modules.adminDelivery.deliveryMethod.helpPanel.packageSlipERP', {}, 'Pakkseddel skrives ut fra ERP (Compass) ved eksport.') },
    { label: t('manager.modules.adminDelivery.deliveryMethod.columns.labelERP', {}, 'Etikett ERP'), sql: 'ShippingLabelsERPYesNo', desc: t('manager.modules.adminDelivery.deliveryMethod.helpPanel.labelERP', {}, 'Fraktetikett skrives ut fra ERP ved eksport.') },
    { label: t('manager.modules.adminDelivery.deliveryMethod.columns.consignmentNoteERP', {}, 'Fraktbrev ERP'), sql: 'ConsignmentNoteERPYesNo', desc: t('manager.modules.adminDelivery.deliveryMethod.helpPanel.consignmentNoteERP', {}, 'Fraktbrev skrives ut fra ERP ved eksport.') }
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {showHelp && (
        <div className="px-4 pt-3">
          <div className="relative rounded-lg border border-blue-200 bg-blue-50 p-4 text-gray-700">
            <button
              type="button"
              onClick={() => setShowHelp(false)}
              className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
              aria-label={t('common.actions.close', {}, 'Lukk')}
            >
              <X className="w-4 h-4" />
            </button>
            <p className="mb-3 text-sm font-semibold text-gray-800">
              {t('manager.modules.adminDelivery.deliveryMethod.helpPanel.title', {}, 'Forklaring av feltene')}
            </p>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">
              {helpFields.map((f, i) => (
                <div key={i} className={`text-sm ${f.inactive ? 'opacity-60' : ''}`}>
                  <dt className="font-medium text-gray-800">
                    {f.label}
                    <code className="ml-2 rounded bg-gray-200 px-1.5 py-0.5 font-mono text-xs text-gray-600">{f.sql}</code>
                    {f.inactive && (
                      <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-700">
                        {t('manager.modules.adminDelivery.deliveryMethod.helpPanel.inactive', {}, 'Ikke aktiv')}
                      </span>
                    )}
                  </dt>
                  <dd className="text-gray-600">{f.desc}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      )}
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={data}
          columns={columns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'delivery-method-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            }
          }}
          selectedRows={selectedIds}
          onSelectionChange={(ids) => setSelectedIds(ids as number[])}
          getRowId={(item) => item.id}
          tableActions={tableActions}
          toolbarRight={
            <button
              type="button"
              onClick={() => setShowHelp(v => !v)}
              className="ml-auto inline-flex items-center gap-2 text-sm text-blue-700 hover:text-blue-900 whitespace-nowrap"
            >
              <HelpCircle className="w-4 h-4" />
              {showHelp
                ? t('manager.modules.adminDelivery.deliveryMethod.helpPanel.hide', {}, 'Skjul feltforklaring')
                : t('manager.modules.adminDelivery.deliveryMethod.helpPanel.show', {}, 'Hva betyr feltene?')}
            </button>
          }
          inlineEditConfig={inlineEditConfig}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {showBulkEditModal && selectedIds.length > 0 && (
        <DeliveryMethodSidePanel
          selectedRows={selectedRowsMap}
          data={data}
          onClose={() => setShowBulkEditModal(false)}
          onSave={() => {
            loadData();
            setSelectedIds([]);
            setShowBulkEditModal(false);
          }}
        />
      )}

      {showCreateModal && (
        <DeliveryMethodCreateModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadData();
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};
